import React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/utils';
import './primitives.css';

/**
 * Icon-only button. `label` is required: it is the accessible name and
 * the tooltip text (shown on hover and on keyboard focus).
 *
 *   <IconButton label="Copy IOC" icon={Copy} onClick={copy} />
 *   <IconButton label="Delete domain" icon={Trash2} variant="danger" />
 *
 *   variant: 'ghost' (default) | 'outline' | 'danger'
 *   size:    'sm' (28px) | 'md' (32px) | 'lg' (36px, touch-friendly)
 *   tooltip: false to skip the tooltip (e.g. when a visible label is adjacent)
 *   pressed: boolean for toggle buttons (sets aria-pressed)
 */
const IconButton = React.forwardRef(function IconButton(
  { label, icon: Icon, variant = 'ghost', size = 'md', tooltip = true, side = 'bottom', pressed, className, children, type = 'button', ...rest },
  ref
) {
  if (process.env.NODE_ENV !== 'production' && !label) {
    // eslint-disable-next-line no-console
    console.warn('IconButton needs a `label` for its accessible name.');
  }
  const button = (
    <button
      ref={ref}
      type={type}
      aria-label={label}
      aria-pressed={pressed === undefined ? undefined : Boolean(pressed)}
      className={cn(
        'is-icon-btn',
        variant !== 'ghost' && `is-icon-btn--${variant}`,
        size !== 'md' && `is-icon-btn--${size}`,
        className
      )}
      {...rest}
    >
      {Icon ? <Icon aria-hidden="true" /> : null}
      {children}
    </button>
  );
  if (!tooltip || !label) return button;
  return (
    <TooltipPrimitive.Provider delayDuration={400} skipDelayDuration={200}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{button}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content side={side} sideOffset={6} collisionPadding={8} className="is-tooltip">
            {label}
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
});

export default IconButton;
